import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState, useEffect } from 'react'
import { BusinessModel } from '@/http/models/business/Business'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { TextInput } from '@/components/atoms/inputs'
import { Spinner } from '@/components/ui/spinner'

interface DomainSettingsProps {
    businessProfile: any | null
    onRefresh: () => Promise<void>
}

export const DomainSettings = ({ businessProfile, onRefresh }: DomainSettingsProps) => {
    const [isSaving, setIsSaving] = useState(false)

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            custom_admin_domain: '',
            custom_customer_domain: '',
            redirect_from_domain: '',
        },
    })

    useEffect(() => {
        if (businessProfile) {
            reset({
                custom_admin_domain: businessProfile.custom_admin_domain || '',
                custom_customer_domain: businessProfile.custom_customer_domain || '',
                redirect_from_domain: businessProfile.redirect_from_domain || '',
            })
        }
    }, [businessProfile, reset])

    const onSubmit = async (data: { custom_admin_domain: string; custom_customer_domain: string; redirect_from_domain: string }) => {
        const body: { custom_admin_domain?: string; custom_customer_domain?: string; redirect_from_domain?: string } = {}
        if (data.custom_admin_domain.trim()) body.custom_admin_domain = data.custom_admin_domain.trim()
        if (data.custom_customer_domain.trim()) body.custom_customer_domain = data.custom_customer_domain.trim()
        if (data.redirect_from_domain.trim()) body.redirect_from_domain = data.redirect_from_domain.trim()

        if (!body.custom_admin_domain && !body.custom_customer_domain && !body.redirect_from_domain) {
            toast.error('At least one domain field is required')
            return
        }

        setIsSaving(true)
        try {
            const response = await BusinessModel.updateCustomDomain({ body })
            if (response.success) {
                toast.success('Custom domain updated successfully')
                await onRefresh()
            } else {
                toast.error((response.error?.data as { message?: string } | undefined)?.message || 'Failed to update custom domain')
            }
        } catch (error) {
            toast.error('An error occurred')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle>Platform domains</CardTitle>
                    <CardDescription>Assigned to your store automatically. Used when no custom domain is set.</CardDescription>
                </CardHeader>
                <CardContent className="pb-0">
                    <div className="flex flex-col gap-1 border-t border-border-subtle py-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-text-2">Storefront</p>
                        <a className="break-all font-mono text-sm text-link hover:underline" href={`https://${businessProfile?.platform_customer_domain || 'N/A'}`}>
                            {businessProfile?.platform_customer_domain || 'N/A'}
                        </a>
                    </div>
                    <div className="flex flex-col gap-1 border-t border-border-subtle py-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-text-2">Admin</p>
                        <a className="break-all font-mono text-sm text-link hover:underline" href={`https://${businessProfile?.platform_admin_domain || 'N/A'}`}>
                            {businessProfile?.platform_admin_domain || 'N/A'}
                        </a>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>DNS setup</CardTitle>
                    <CardDescription>
                        To connect a custom domain, add a CNAME record at your domain provider that points to the target below.
                    </CardDescription>
                </CardHeader>
                <CardContent className="pb-0">
                    <div className="flex flex-col gap-1 border-t border-border-subtle py-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-text-2">Custom storefront domain</p>
                        <p className="break-all font-mono text-sm text-ink">CNAME: {businessProfile?.platform_customer_domain || 'N/A'}</p>
                    </div>
                    <div className="flex flex-col gap-1 border-t border-border-subtle py-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-text-2">Custom admin domain</p>
                        <p className="break-all font-mono text-sm text-ink">CNAME: {businessProfile?.platform_admin_domain || 'N/A'}</p>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Custom domains</CardTitle>
                    <CardDescription>Domains you own, pointed at your store.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <TextInput
                        control={control}
                        name="custom_customer_domain"
                        label="Storefront domain"
                        placeholder="www.yourstore.com"
                        errors={errors}
                    />
                    <TextInput
                        control={control}
                        name="custom_admin_domain"
                        label="Admin domain"
                        placeholder="admin.yourstore.com"
                        errors={errors}
                    />
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Redirect</CardTitle>
                    <CardDescription>
                        Visitors to this hostname are sent to your custom storefront domain, e.g. a bare domain to www,
                        or an old domain to your current one.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <TextInput
                        control={control}
                        name="redirect_from_domain"
                        label="Redirect from"
                        placeholder="xyz.com"
                        errors={errors}
                    />
                </CardContent>
            </Card>

            <div className="flex justify-end">
                <Button onClick={handleSubmit(onSubmit)} disabled={isSaving}>
                    {isSaving && <Spinner />}
                    Save
                </Button>
            </div>
        </div>
    )
}
